import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../utils/apiClient';
import { handleAuthError } from '../utils/authErrorHandler';
import EntityRequestManager from './EntityRequestManager';
import SimpleInvitations from './SimpleInvitations';
import UsageDashboard from './UsageDashboard';
import './AdminDashboard.css';

const AdminDashboard = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateUser, setShowCreateUser] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  // Debug: Log user info when component loads
  useEffect(() => {
    //console.log('🔍 [AdminDashboard] User object:', user);
    //console.log('🔍 [AdminDashboard] User role:', user?.role);
    //console.log('🔍 [AdminDashboard] User tenantId:', user?.tenantId);
    //console.log('🔍 [AdminDashboard] User organizationName:', user?.organizationName);
  }, [user]);

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    try {
      // Get tenantId from connected user
      const tenantId = user?.tenantId || user?.organizationId || user?.orgId;
      if (!tenantId) {
        throw new Error('TenantId manquant pour l’admin');
      }

      // Charger les stats de l'organisation (utiliser la route session-compatible)
      const statsResponse = await apiClient.get(`/api/admin-session/org-stats`);
      // Adapt mapping for retro-compatibility
      setStats({
        usersCount: statsResponse.data?.stats?.userCount || statsResponse.data?.users?.length || 0,
        graphsCount: 1, // If you want to display number of graphs, adapt according to backend
        nodesCount: statsResponse.data?.stats?.nodeCount || 0,
        relationsCount: statsResponse.data?.stats?.relationshipCount || 0
      });

      // Charger les utilisateurs de l'organisation
      setUsers(statsResponse.data?.users || []);

    } catch (error) {
      if (handleAuthError(error)) return;
      console.error('Error loading admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-dashboard">     
      {/* Header */}
      <div className="dashboard-header" style={{ marginTop: '40px' }}>
        <div className="header-left">
          <h1>{t('adminDashboard.title')}</h1>
          <p>{user.organizationName}</p>
        </div>
        <div className="header-right">
          <div className="user-info">
            <span>{user.firstName} {user.lastName}</span>
            <span className="user-role">{t('adminDashboard.adminRole')}</span>
          </div>
          <button
            className="btn-dashboard-global"
            onClick={() => window.location.href = '/'}
            style={{marginRight: 12}}
          >
            {t('adminDashboard.globalDashboard')}
          </button>
          <button onClick={onLogout} className="btn-logout">
            {t('adminDashboard.logout')}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <div className="dashboard-nav">
        <button 
          className={activeTab === 'overview' ? 'active' : ''}
          onClick={() => setActiveTab('overview')}
        >
          📊 {t('adminDashboard.overview')}
        </button>
        <button 
          className={activeTab === 'users' ? 'active' : ''}
          onClick={() => setActiveTab('users')}
        >
          👥 {t('adminDashboard.users')}
        </button>
        <button 
          className={activeTab === 'requests' ? 'active' : ''}
          onClick={() => setActiveTab('requests')}
        >
          🔔 {t('adminDashboard.requests')}
        </button>
        <button 
          className={activeTab === 'graphs' ? 'active' : ''}
          onClick={() => setActiveTab('graphs')}
        >
          🕸️ {t('adminDashboard.graphs')}
        </button>
        <button
          className={activeTab === 'invites' ? 'active' : ''}
          onClick={() => setActiveTab('invites')}
        >
          🔑 {t('adminDashboard.invites')}
        </button>
        <button
          className={activeTab === 'usage' ? 'active' : ''}
          onClick={() => setActiveTab('usage')}
        >
          📊 {t('adminDashboard.usageAnalytics')}
        </button>
        <button 
          className={activeTab === 'settings' ? 'active' : ''}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ {t('adminDashboard.settings')}
        </button>
      </div>

      {/* Contenu */}
      <div className="dashboard-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <h2>📊 {t('adminDashboard.overviewOrg')}</h2>
            
            {loading ? (
              <div className="loading">{t('adminDashboard.loading')}</div>
            ) : (
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>{t('adminDashboard.users')}</h3>
                  <div className="stat-number">{stats?.usersCount || 0}</div>
                  <div className="stat-detail">{t('adminDashboard.activeAccounts')}</div>
                </div>
                <div className="stat-card">
                  <h3>{t('adminDashboard.graphs')}</h3>
                  <div className="stat-number">{stats?.graphsCount || 0}</div>
                  <div className="stat-detail">{t('adminDashboard.projectsCreated')}</div>
                </div>
                <div className="stat-card">
                  <h3>{t('adminDashboard.nodes')}</h3>
                  <div className="stat-number">{stats?.nodesCount || 0}</div>
                  <div className="stat-detail">{t('adminDashboard.totalEntities')}</div>
                </div>
                <div className="stat-card">
                  <h3>{t('adminDashboard.relations')}</h3>
                  <div className="stat-number">{stats?.relationsCount || 0}</div>
                  <div className="stat-detail">{t('adminDashboard.connections')}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-tab">
            <div className="tab-header">
              <h2>👥 {t('adminDashboard.manageUsers')}</h2>
              <button 
                onClick={() => setShowCreateUser(true)}
                className="btn-primary"
              >
                ➕ {t('adminDashboard.newUser')}
              </button>
            </div>

            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>{t('adminDashboard.name')}</th>
                    <th>{t('adminDashboard.email')}</th>
                    <th>{t('adminDashboard.role')}</th>
                    <th>{t('adminDashboard.status')}</th>
                    <th>{t('adminDashboard.lastLogin')}</th>
                    <th>{t('adminDashboard.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(user => (
                    <tr key={user._id}>
                      <td>{user.firstName} {user.lastName}</td>
                      <td>{user.email}</td>
                      <td>
                        <span className={`role-badge ${user.role}`}>
                          {user.role}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge ${user.status}`}>
                          {user.status}
                        </span>
                      </td>
                      <td>
                        {user.lastLoginAt ? 
                          new Date(user.lastLoginAt).toLocaleDateString() : 
                          t('adminDashboard.never')
                        }
                      </td>
                      <td>
                        <button className="btn-edit">✏️</button>
                        <button className="btn-delete">🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="requests-tab">
            <EntityRequestManager user={user} />
          </div>
        )}

        {activeTab === 'graphs' && (
          <div className="graphs-tab">
            <h2>🕸️ {t('adminDashboard.orgGraphs')}</h2>
            <p>{t('adminDashboard.featureInDevelopment')}</p>
          </div>
        )}

        {activeTab === 'invites' && (
          <div className="invites-tab">
            <div style={{ marginTop: 20 }}>
              <p>{t('adminDashboard.createShareInviteCodes')}</p>
            </div>
            <div style={{ marginTop: 12 }}>
              <SimpleInvitations />
            </div>
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="usage-tab">
            <h2>📊 {t('adminDashboard.usageAnalytics')}</h2>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              {t('adminDashboard.visualizeUsage')}
            </p>
            <UsageDashboard user={user} />
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="settings-tab">
            <h2>⚙️ {t('adminDashboard.orgSettings')}</h2>
            <p>{t('adminDashboard.featureInDevelopment')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;